import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { COIN_META } from "@/components/layernav/navConfig";
import { detectAssets } from "@/components/article/MarketContext";
import { AreaChart } from "./AreaChart";

const RANGES = [
  { key: "7d", label: "7D", words: "7-day" },
  { key: "30d", label: "30D", words: "30-day" },
] as const;

/* Inline data module (§21 / Q3 refinement): a real longer-horizon view that
   supports the editorial argument — deliberately different from the rail's
   24H Market Context. No dataset → no chart. */
export const InlineAssetChart = ({ article }: { article: Article }) => {
  const ctx: any = article.context;
  const family = ctx?.family || "GENERAL";
  const sym = ctx?.asset || ctx?.assets_detected?.[0] || detectAssets(article)[0] || null;
  const [range, setRange] = useState<"7d" | "30d">("30d");

  const { data } = useQuery({
    queryKey: ["asset-chart", sym, range],
    queryFn: async () => (await api.get(`/markets/${sym}?range=${range}`)).data,
    staleTime: 5 * 60 * 1000,
    enabled: !!sym && family !== "SECURITY",
    retry: 1,
  });

  if (!sym || family === "SECURITY" || !data) return null;
  const points = (data.chart || []) as { t: number; price: number }[];
  if (points.length < 2) return null;

  const first = points[0].price, last = points[points.length - 1].price;
  const delta = first > 0 ? ((last - first) / first) * 100 : 0;
  const up = delta >= 0;
  const name = data.name || COIN_META[sym]?.name || sym;
  const words = RANGES.find((r) => r.key === range)!.words;
  const summary = `${name} moved from $${first.toLocaleString()} to $${last.toLocaleString()} over the last ${words} period, ${
    up ? "a gain" : "a decline"} of ${Math.abs(delta).toFixed(1)} percent.`;

  return (
    <figure data-testid="inline-asset-chart" data-track="inline_chart"
      className="my-8 rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <figcaption className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-gray-300">
          {name} · {words} price
          <span className={`ml-2.5 ${up ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
            {up ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
          </span>
        </figcaption>
        <div className="flex items-center gap-1 rounded-lg border border-white/[0.08] p-0.5" role="tablist" aria-label="Chart timeframe">
          {RANGES.map((r) => (
            <button key={r.key} type="button" role="tab" aria-selected={range === r.key}
              onClick={() => setRange(r.key)} data-testid={`inline-chart-range-${r.key}`}
              className={`rounded-md px-2 py-0.5 font-mono text-[10px] font-bold uppercase transition-colors ${
                range === r.key ? "bg-white/[0.08] text-white" : "text-gray-500 hover:text-gray-300"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3.5">
        <AreaChart points={points} up={up} height={130} xMode="day" ariaLabel={summary} testid="inline-chart-svg" />
      </div>
      <p className="sr-only">{summary}</p>
      <p className="mt-2.5 font-mono text-[9px] text-gray-600 uppercase tracking-wider">
        Source: Kraken · NewsLayer Markets{data.updated_at ? ` · Updated ${timeAgo(data.updated_at)}` : ""}
      </p>
    </figure>
  );
};
